import api, { getAccessToken } from 'common/api';
import runningInDev from 'utils/runningInDev';
import { withoutTrailingSlash } from 'utils/path';

const SIGNALR_DEV_BASE = 'https://sr-tv-v2-dev.service.signalr.net';
const SIGNALR_PROD_BASE = 'https://sr-tv-v2-prod.service.signalr.net';

const resolveSignalRBaseUrl = () => {
  const explicit =
    process.env.REACT_APP_RTV_SIGNALR_BASE ||
    process.env.REACT_APP_RTV_SIGNALR_URL ||
    process.env.REACT_APP_SIGNALR_BASE ||
    process.env.REACT_APP_SIGNALR_URL;

  if (explicit) {
    return withoutTrailingSlash(explicit);
  }

  const apiBase = api.getApiBaseUrl();
  if (typeof apiBase === 'string') {
    if (apiBase.includes('api.dev.trialviewlive.com')) return SIGNALR_DEV_BASE;
    if (apiBase.includes('api.trialviewlive.com')) return SIGNALR_PROD_BASE;
  }

  return runningInDev ? SIGNALR_DEV_BASE : SIGNALR_PROD_BASE;
};

const parseResponseBody = async (response: Response) => {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
};

type NegotiateResult = {
  connectionId: string;
  raw: any;
};

export async function negotiateRealTime({
  caseId,
  signal,
}: {
  caseId: string;
  signal?: AbortSignal;
}): Promise<NegotiateResult> {
  const accessToken = getAccessToken();
  if (!accessToken) {
    throw new Error('Missing access token for realtime negotiate');
  }

  const baseUrl = resolveSignalRBaseUrl();
  const hub = encodeURIComponent(caseId);
  const url = `${baseUrl}/client/negotiate?hub=${hub}&negotiateVersion=1`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    signal,
  });

  const body = await parseResponseBody(response);

  if (!response.ok) {
    const message = body?.error || body?.message || response.statusText || 'Negotiate failed';
    throw new Error(message);
  }

  const connectionId = body?.connectionId ?? body?.connection_id;
  if (!connectionId) {
    throw new Error('Negotiate response missing connectionId');
  }

  return { connectionId, raw: body };
}

export async function bootstrapRealTime({
  caseId,
  connectionId,
  signal,
}: {
  caseId: string;
  connectionId: string;
  signal?: AbortSignal;
}) {
  const path = `/triggers/cases/${encodeURIComponent(caseId)}/realtime-transcripts/bootstrap`;
  return api.post(path, { connectionId }, { signal });
}

export async function fetchOldTranscripts({
  caseId,
  fromDate,
  signal,
}: {
  caseId: string;
  fromDate: string;
  signal?: AbortSignal;
}) {
  const params = new URLSearchParams();
  params.set('status', 'ended');
  params.set('from', fromDate);
  const path = `/triggers/cases/${encodeURIComponent(caseId)}/realtime-transcripts/sessions?${params.toString()}`;
  return api.get(path, { signal });
}

export async function fetchEndedSessions({
  caseId,
  signal,
}: {
  caseId: string;
  signal?: AbortSignal;
}) {
  const path = `/triggers/cases/${encodeURIComponent(caseId)}/realtime-transcripts/sessions?isActive=false`;
  return api.get(path, { signal });
}

export async function fetchSessionTranscripts({
  caseId,
  sessionKey,
  signal,
}: {
  caseId: string;
  sessionKey: string;
  signal?: AbortSignal;
}) {
  const path = `/triggers/cases/${encodeURIComponent(caseId)}/realtime-transcripts/sessions/${encodeURIComponent(sessionKey)}/transcripts`;
  return api.get(path, { signal });
}
